import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { Typography, Tooltip, IconButton, Snackbar, MenuItem, Select, FormControl, Box, Badge, Grid, Paper, Tabs, Tab } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import DateRangePickerOmni from 'allGlobalComponents/DateRangePickerOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import SearchableDropdown from 'allGlobalComponents/SearchableDropdown';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SyncIcon from '@mui/icons-material/Sync';
import FilterListIcon from '@mui/icons-material/FilterList';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import GetAppIcon from '@mui/icons-material/GetApp';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import EmailCampaignChart from '../charts/EmailCampaignChart';
import { globalDateFormat } from 'app/constants';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';

const useStyles = makeStyles(theme => ({
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
        gap: theme.spacing(2)
    },
    leftControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    rightControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5)
    },
    datePickerContainer: {
        position: 'relative',
        zIndex: 1000
    },
    chartSection: {
        marginBottom: theme.spacing(3)
    },
    tableSection: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        overflow: 'hidden'
    },
    statusChip: {
        padding: '4px 12px',
        borderRadius: 16,
        fontSize: 12,
        fontWeight: 500,
        display: 'inline-block',
        textTransform: 'capitalize'
    },
    statusSuccess: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)',
        color: theme.palette.type === 'dark' ? '#4ADE80' : '#16A34A'
    },
    statusDelivered: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)',
        color: theme.palette.type === 'dark' ? '#34D399' : '#059669'
    },
    statusQueue: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)',
        color: theme.palette.type === 'dark' ? '#FBBF24' : '#D97706'
    },
    statusFailed: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
        color: theme.palette.type === 'dark' ? '#F87171' : '#DC2626'
    },
    statusReceived: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
        color: theme.palette.type === 'dark' ? '#A78BFA' : '#7C3AED'
    },
    buttonIcon: {
        marginRight: theme.spacing(0.5),
        fontSize: 18
    },
    refreshButton: {
        padding: 8,
        color: theme.palette.type === 'dark' ? '#FFFFFF' : '#000000',
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        },
        '& .MuiSvgIcon-root': {
            fontSize: 24,
            fontWeight: 'bold'
        }
    },
    typeSelect: {
        minWidth: 150,
        '& .MuiSelect-select': {
            padding: theme.spacing(1, 2),
            backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
            borderRadius: 8
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main
        }
    },
    selectContainer: {
        display: 'flex',
        alignItems: 'center'
    },
    placeholderText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 300,
        color: theme.palette.text.secondary,
        fontSize: 16,
        fontStyle: 'italic'
    },
    viewText: {
        color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
        fontWeight: 500,
        fontSize: '1.2rem',
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        '&:hover': {
            color: theme.palette.type === 'dark' ? '#D1D5DB' : '#374151',
            textDecoration: 'underline'
        }
    },
    htmlPreviewContent: {
        color: theme.palette.text.primary,
        fontSize: 14,
        lineHeight: 1.6,
        padding: theme.spacing(1),
        '& img': {
            maxWidth: '100%',
            height: 'auto'
        },
        '& a': {
            color: theme.palette.primary.main,
            cursor: 'pointer',
            textDecoration: 'underline'
        },
        '& ul': {
            listStyleType: 'disc',
            paddingLeft: 40,
            marginLeft: 0
        },
        '& ol': {
            listStyleType: 'decimal',
            paddingLeft: 40,
            marginLeft: 0
        },
        '& li': {
            display: 'list-item',
            marginBottom: theme.spacing(0.5)
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: theme.palette.text.primary,
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1)
        },
        '& p': {
            marginBottom: theme.spacing(1)
        }
    },
    noHtmlText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        fontSize: 14
    },
    filterContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5)
    },
    filterRow: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column'
        }
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
        gap: theme.spacing(2)
    },
    overviewCard: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        borderRadius: 16,
        height: '100%',
        boxShadow: theme.palette.type === 'dark'
            ? '0 4px 20px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.08)'
    },
    overviewTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(3),
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '1.1rem'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(2, 1fr)'
        }
    },
    statCard: {
        padding: theme.spacing(2.5),
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        cursor: 'default',
        border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}`,
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.palette.type === 'dark'
                ? '0 8px 24px rgba(0,0,0,0.4)'
                : '0 8px 24px rgba(0,0,0,0.12)'
        }
    },
    statIconWrapper: {
        width: 48,
        height: 48,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(1.5)
    },
    statIcon: {
        fontSize: 24
    },
    statValue: {
        fontSize: '2rem',
        fontWeight: 800,
        lineHeight: 1.1,
        marginBottom: theme.spacing(0.5)
    },
    statLabel: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        opacity: 0.7
    },
    attachmentCountChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: '4px 10px',
        borderRadius: 16,
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
        color: theme.palette.type === 'dark' ? '#60A5FA' : '#2563EB'
    },
    attachmentTabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2)
    },
    attachmentTab: {
        minHeight: 48,
        textTransform: 'none',
        fontSize: 13,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
            color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main
        }
    },
    attachmentPreviewContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        padding: theme.spacing(3),
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`
    },
    attachmentImage: {
        maxWidth: '100%',
        maxHeight: 400,
        borderRadius: 8,
        objectFit: 'contain'
    },
    attachmentVideo: {
        maxWidth: '100%',
        maxHeight: 400,
        borderRadius: 8
    },
    attachmentFileInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(4)
    },
    attachmentFileIcon: {
        fontSize: 80,
        color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280'
    },
    attachmentFileName: {
        fontSize: 16,
        fontWeight: 500,
        color: theme.palette.text.primary,
        textAlign: 'center',
        wordBreak: 'break-all'
    },
    attachmentFileType: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase'
    },
    downloadButton: {
        marginTop: theme.spacing(2)
    },
    noAttachmentText: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        fontSize: 14
    },
    filterFieldHalf: {
        flex: 1,
        minWidth: 150
    },
    filterFieldFull: {
        width: '100%'
    },
    loadingText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        marginTop: theme.spacing(0.5)
    }
}));

// Status options for the dropdown filter
const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Queue', value: 'queue' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Received', value: 'received' },
    { label: 'Success', value: 'success' },
    { label: 'Failed', value: 'failed' },
];

// Helper function to get file type from URL or filename
const getFileType = (url, filename) => {
    const name = filename || url || '';
    const extension = name.split('.').pop()?.toLowerCase();

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'wmv', 'flv'];
    const pdfExtensions = ['pdf'];
    const docExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (pdfExtensions.includes(extension)) return 'pdf';
    if (docExtensions.includes(extension)) return 'document';
    return 'file';
};

// Helper function to get file icon based on type
const getFileIcon = (fileType, classes) => {
    switch (fileType) {
        case 'image':
            return <ImageIcon className={classes.attachmentFileIcon} />;
        case 'video':
            return <VideoLibraryIcon className={classes.attachmentFileIcon} />;
        case 'pdf':
            return <PictureAsPdfIcon className={classes.attachmentFileIcon} />;
        case 'document':
            return <DescriptionIcon className={classes.attachmentFileIcon} />;
        default:
            return <InsertDriveFileIcon className={classes.attachmentFileIcon} />;
    }
};

// Helper function to get filename from attachment
const getAttachmentFilename = (attachment) => {
    if (typeof attachment === 'string') {
        return attachment.split('/').pop() || 'Attachment';
    }
    return attachment.filename || attachment.name || attachment.url?.split('/').pop() || 'Attachment';
};

// Helper function to get attachment URL
const getAttachmentUrl = (attachment) => {
    if (typeof attachment === 'string') {
        return attachment;
    }
    return attachment.url || attachment;
};

function OutboxTab({ userData = null }) {
    const theme = useTheme();
    const classes = useStyles();
    const csvLink = useRef();

    const [outboxType, setOutboxType] = useState('push');
    const [isLoading, setLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [data, setData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [dataForExport, setDataForExport] = useState([]);
    const [currentParams, setCurrentParams] = useState({ limit: 10, page: 0 });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const [dateRange, setDateRange] = useState({
        startDate: moment.utc().startOf('day'),
        endDate: moment.utc().endOf('day')
    });

    // HTML Preview Modal State
    const [htmlPreviewOpen, setHtmlPreviewOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);

    // Attachment Preview Modal State
    const [attachmentPreviewOpen, setAttachmentPreviewOpen] = useState(false);
    const [selectedAttachments, setSelectedAttachments] = useState([]);
    const [activeAttachmentTab, setActiveAttachmentTab] = useState(0);

    // Filter Dialog State
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [localStatusFilter, setLocalStatusFilter] = useState('');
    const [campaignIdFilter, setCampaignIdFilter] = useState('');
    const [localCampaignIdFilter, setLocalCampaignIdFilter] = useState('');

    // Sender Email Filter State
    const [senderEmails, setSenderEmails] = useState([]);
    const [loadingSenderEmails, setLoadingSenderEmails] = useState(false);
    const [selectedSenderEmail, setSelectedSenderEmail] = useState(null);
    const [localSelectedSenderEmail, setLocalSelectedSenderEmail] = useState(null);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Fetch sender emails for filter dropdown
    const fetchSenderEmails = () => {
        setLoadingSenderEmails(true);
        const apiPayload = {};
        if (userData?.id) apiPayload.clientId = userData?.id;

        CoreHttpHandler.request(
            'emailCampaign',
            'senderEmailFetch',
            apiPayload,
            response => {
                const emails = response.data?.data?.data?.emailsList || [];
                setSenderEmails(emails);
                setLoadingSenderEmails(false);
            },
            error => {
                console.error('Error fetching sender emails:', error);
                setSenderEmails([]);
                setLoadingSenderEmails(false);
            }
        );
    };

    // Helper for single row export (preserve newlines for Excel multi-line cells)
    const sanitizeForSingleExport = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value !== 'string') return String(value);

        return value
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n') || '-';
    };


    // Handle single row download
    const handleDownloadSingleRow = (row) => {
        const filename = `outbox_${row.id || 'export'}_${moment().format('YYYY-MM-DD')}.csv`;

        // Escape value for CSV - handle quotes and preserve newlines
        const escapeCSV = (value) => {
            if (value === null || value === undefined) return '"-"';
            const str = String(value);
            const escaped = str.replace(/"/g, '""');
            return `"${escaped}"`;
        };

        const formattedRow = {
            'ID': row.id || '-',
            'Campaign Id': row.campaign_id || '-',
            'Sender Email': row.sender_email || '-',
            'Receiver Email': row.receiver_email || '-',
            'Subject': row.subject || '-',
            'Status': capitalizeWords(row.status),
            'Email Text': sanitizeForSingleExport(row.text),
            'Email HTML': sanitizeForSingleExport(row.html),
            'Total': row.total_count ?? 0,
            'Success': row.success_count ?? 0,
            'Delivered': row.delivered_count ?? 0,
            'Received': row.received_count ?? 0,
            'Queue': row.queue_count ?? 0,
            'Failed': row.failed_count ?? 0,
            'Compliant': row.compliant_count ?? 0,
            'Delivery %': calculateDeliveryPercentage(row),
            'Attachments': row.attachments ? (Array.isArray(row.attachments) ? row.attachments.length : 0) : 0,
            'Date': row.dt ? moment(row.dt).format(globalDateFormat) : '-',
            'Response Message': sanitizeForSingleExport(row.error_message)
        };

        const headers = Object.keys(formattedRow);
        const values = Object.values(formattedRow);

        const headerLine = headers.map(h => escapeCSV(h)).join(',');
        const valueLine = values.map(v => escapeCSV(v)).join(',');

        const csvContent = headerLine + '\r\n' + valueLine;

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    // Format sender emails for dropdown
    const formattedSenderEmailOptions = senderEmails.map(e => ({
        value: e.id,
        label: `${e.email} (${e.sender_domain_name})`,
        sender_domain_id: e.sender_domain_id
    }));

    // Calculate active filter count
    const getActiveFilterCount = () => {
        let count = 0;
        if (statusFilter && statusFilter !== '') count++;
        if (campaignIdFilter && campaignIdFilter.trim() !== '') count++;
        if (selectedSenderEmail) count++;
        return count;
    };

    // Calculate status counts from data
    const getStatusCounts = () => ({
        success: data.filter(e => e.status?.toLowerCase() === 'success').length,
        delivered: data.filter(e => e.status?.toLowerCase() === 'delivered').length,
        queue: data.filter(e => e.status?.toLowerCase() === 'queue').length,
        failed: data.filter(e => e.status?.toLowerCase() === 'failed').length,
        received: data.filter(e => e.status?.toLowerCase() === 'received').length
    });

    const statusCounts = getStatusCounts();

    // Stats configuration
    const statsConfig = [
        {
            label: 'Total Emails',
            value: totalItems,
            color: theme.palette.type === 'dark' ? '#E5E7EB' : '#374151',
            bgColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            iconBg: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            icon: <EmailIcon className={classes.statIcon} />
        },
        {
            label: 'Queue',
            value: statusCounts.queue,
            color: '#F59E0B',
            bgColor: theme.palette.type === 'dark' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.08)',
            iconBg: 'rgba(245, 158, 11, 0.2)',
            icon: <HourglassEmptyIcon className={classes.statIcon} />
        },
        {
            label: 'Delivered',
            value: statusCounts.delivered,
            color: '#10B981',
            bgColor: theme.palette.type === 'dark' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.08)',
            iconBg: 'rgba(16, 185, 129, 0.2)',
            icon: <MarkEmailReadIcon className={classes.statIcon} />
        },
        {
            label: 'Received',
            value: statusCounts.received,
            color: '#8B5CF6',
            bgColor: theme.palette.type === 'dark' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.08)',
            iconBg: 'rgba(139, 92, 246, 0.2)',
            icon: <CallReceivedIcon className={classes.statIcon} />
        },
        {
            label: 'Success',
            value: statusCounts.success,
            color: '#22C55E',
            bgColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.08)',
            iconBg: 'rgba(34, 197, 94, 0.2)',
            icon: <CheckCircleOutlineIcon className={classes.statIcon} />
        },
        {
            label: 'Failed',
            value: statusCounts.failed,
            color: '#EF4444',
            bgColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
            iconBg: 'rgba(239, 68, 68, 0.2)',
            icon: <ErrorOutlineIcon className={classes.statIcon} />
        },
    ];

    // Sync local filter when dialog opens
    useEffect(() => {
        if (filterDialogOpen) {
            setLocalStatusFilter(statusFilter);
            setLocalCampaignIdFilter(campaignIdFilter);
            setLocalSelectedSenderEmail(selectedSenderEmail);
            // Fetch sender emails when filter dialog opens
            if (senderEmails.length === 0) {
                fetchSenderEmails();
            }
        }
    }, [filterDialogOpen]);

    const fetchData = () => {
        if (!outboxType) return;

        setLoading(true);
        const apiPayload = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            limit: currentParams.limit,
            page: currentParams.page
        };

        // Set campaign_id based on outbox type (only if no filter is applied)
        if (campaignIdFilter && campaignIdFilter.trim() !== '') {
            apiPayload.campaign_id = campaignIdFilter.trim();
        } else {
            if (outboxType === 'push') {
                apiPayload.campaign_id = 0;
            } else if (outboxType === 'schedule') {
                apiPayload.campaign_id = -1;
            }
            // For 'all', don't send campaign_id
        }

        // Add status filter if selected
        if (statusFilter && statusFilter !== '') {
            apiPayload.status = [statusFilter];
        }

        // Add sender email filter - send both sender_domain_id and sender_email_id
        if (selectedSenderEmail) {
            apiPayload.sender_domain_id = selectedSenderEmail.sender_domain_id;
            apiPayload.sender_email_id = selectedSenderEmail.value;
        }

        if (userData?.id) {
            apiPayload.clientId = userData?.id;
        }

        CoreHttpHandler.request(
            'emailCampaign',
            'OutboundEmailsList',
            apiPayload,
            response => {
                const res = response.data.data.data.emailsList || [];
                const totalCount = response.data.data.data.totalItems || 0;
                setData(res);
                setTotalItems(totalCount);
                setLoading(false);
            },
            error => {
                console.error('Error fetching emails:', error);
                setData([]);
                setTotalItems(0);
                setLoading(false);
                showSnackbar('Failed to fetch emails', 'error');
            }
        );
    };

    const fetchAllDataForExport = () => {
        return new Promise((resolve, reject) => {
            const apiPayload = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                limit: totalItems || 10000,
                page: 0
            };

            // Set campaign_id based on outbox type (only if no filter is applied)
            if (campaignIdFilter && campaignIdFilter.trim() !== '') {
                apiPayload.campaign_id = campaignIdFilter.trim();
            } else {
                if (outboxType === 'push') {
                    apiPayload.campaign_id = 0;
                } else if (outboxType === 'schedule') {
                    apiPayload.campaign_id = -1;
                }
                // For 'all', don't send campaign_id
            }

            // Add status filter if selected
            if (statusFilter && statusFilter !== '') {
                apiPayload.status = [statusFilter];
            }

            // Add sender email filter - send both sender_domain_id and sender_email_id
            if (selectedSenderEmail) {
                apiPayload.sender_domain_id = selectedSenderEmail.sender_domain_id;
                apiPayload.sender_email_id = selectedSenderEmail.value;
            }

            if (userData?.id) {
                apiPayload.clientId = userData?.id;
            }

            CoreHttpHandler.request(
                'emailCampaign',
                'OutboundEmailsList',
                apiPayload,
                response => {
                    const res = response.data.data.data.emailsList || [];
                    resolve(res);
                },
                error => {
                    reject(error);
                }
            );
        });
    };

    const generateFileName = () => {
        const today = moment().format('YYYY-MM-DD');
        const typeLabel = outboxType || 'all';
        const statusLabel = statusFilter ? `_${statusFilter}` : '';
        return `outbox_${typeLabel}${statusLabel}_${dateRange.startDate.format('YYYY-MM-DD')}_to_${dateRange.endDate.format('YYYY-MM-DD')}_${today}.csv`;
    };

    const formatDataForExport = (rawData) => {
        if (!rawData || rawData.length === 0) return [];
        return rawData.map((item, index) => ({
            'S.No': index + 1,
            'ID': item.id || '-',
            'Sender Email': item.sender_email || '-',
            'Receiver Email': item.receiver_email || '-',
            'Subject': item.subject || '-',
            'Status': capitalizeWords(item.status),
            'Attachments': item.attachments ? (Array.isArray(item.attachments) ? item.attachments.length : 0) : 0,
            'Date': item.dt ? moment(item.dt).format(globalDateFormat) : '-',
            'Error Message': item.error_message || '-'
        }));
    };

    const handleExport = async () => {
        if (!outboxType) {
            showSnackbar('Please select an outbox type first', 'warning');
            return;
        }
        try {
            setIsExporting(true);
            const exportData = await fetchAllDataForExport();
            if (exportData.length === 0) {
                showSnackbar('No data available for export', 'warning');
                setIsExporting(false);
                return;
            }

            // Escape value for CSV
            const escapeCSV = (value) => {
                if (value === null || value === undefined) return '"-"';
                let str = String(value);
                // Remove all newlines and carriage returns
                str = str.replace(/\r\n/g, ' ').replace(/\r/g, ' ').replace(/\n/g, ' ');
                // Replace multiple spaces with single space
                str = str.replace(/\s+/g, ' ').trim();
                // Escape quotes by doubling them
                str = str.replace(/"/g, '""');
                return `"${str}"`;
            };

            const headers = [
                'ID', 'Campaign Id', 'Sender Email', 'Receiver Email', 'Subject', 'Status',
                'Email Text', 'Email HTML', 'Total', 'Success', 'Delivered',
                'Received', 'Queue', 'Failed', 'Compliant', 'Delivery %',
                'Attachments', 'Date', 'Response Message'
            ];

            const rows = exportData.map(item => [
                item.id || '-',
                item.campaign_id || '-',
                item.sender_email || '-',
                item.receiver_email || '-',
                item.subject || '-',
                capitalizeWords(item.status),
                item.text || '-',
                item.html || '-',
                item.total_count ?? 0,
                item.success_count ?? 0,
                item.delivered_count ?? 0,
                item.received_count ?? 0,
                item.queue_count ?? 0,
                item.failed_count ?? 0,
                item.compliant_count ?? 0,
                calculateDeliveryPercentage(item),
                item.attachments ? (Array.isArray(item.attachments) ? item.attachments.length : 0) : 0,
                item.dt ? moment(item.dt).format(globalDateFormat) : '-',
                item.error_message || '-'
            ]);

            // Build CSV content
            const headerLine = headers.map(h => escapeCSV(h)).join(',');
            const dataLines = rows.map(row => row.map(cell => escapeCSV(cell)).join(',')).join('\r\n');
            const csvContent = headerLine + '\r\n' + dataLines;

            // BOM for Excel UTF-8 support
            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', generateFileName());
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setIsExporting(false);
        } catch (error) {
            console.error('Export failed:', error);
            showSnackbar('Export failed. Please try again.', 'error');
            setIsExporting(false);
        }
    };

    const handleDateChange = ({ startDate, endDate }) => {
        if (startDate && endDate) {
            setDateRange({ startDate, endDate });
            setCurrentParams({ ...currentParams, page: 0 });
        }
    };

    const handleTypeChange = (event) => {
        const value = event.target.value;
        setOutboxType(value);
        setCurrentParams({ limit: 10, page: 0 });
        setData([]);
        setTotalItems(0);
    };

    const capitalizeWords = (str) => {
        if (!str || typeof str !== 'string') return '-';
        return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };

    const getStatusClass = (status) => {
        const statusLower = (status || '').toLowerCase();
        switch (statusLower) {
            case 'success': return classes.statusSuccess;
            case 'delivered': return classes.statusDelivered;
            case 'queue': return classes.statusQueue;
            case 'failed': return classes.statusFailed;
            case 'received': return classes.statusReceived;
            default: return '';
        }
    };

    const handleRefresh = () => {
        if (outboxType) {
            fetchData();
        }
    };

    // Handle HTML preview
    const handleViewHtml = (row) => {
        setSelectedEmail(row);
        setHtmlPreviewOpen(true);
    };

    const handleCloseHtmlPreview = () => {
        setHtmlPreviewOpen(false);
        setSelectedEmail(null);
    };

    // Handle Attachment preview
    const handleViewAttachments = (row) => {
        const attachments = row.attachments || [];
        setSelectedAttachments(attachments);
        setActiveAttachmentTab(0);
        setAttachmentPreviewOpen(true);
    };

    const handleCloseAttachmentPreview = () => {
        setAttachmentPreviewOpen(false);
        setSelectedAttachments([]);
        setActiveAttachmentTab(0);
    };

    const handleAttachmentTabChange = (event, newValue) => {
        setActiveAttachmentTab(newValue);
    };

    const handleDownloadAttachment = (attachment) => {
        const url = getAttachmentUrl(attachment);
        const filename = getAttachmentFilename(attachment);

        // Force download using fetch and blob
        fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(error => {
                console.error('Download failed:', error);
                // Fallback to opening in new tab if fetch fails
                window.open(url, '_blank');
            });
    };

    // Extract body content from HTML
    const extractBodyContent = (html) => {
        if (!html) return '';
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (bodyMatch) return bodyMatch[1];
        return html;
    };

    // Get attachment count
    const getAttachmentCount = (attachments) => {
        if (!attachments) return 0;
        if (Array.isArray(attachments)) return attachments.length;
        return 0;
    };

    // Filter Dialog Handlers
    const handleOpenFilterDialog = () => {
        setFilterDialogOpen(true);
    };

    const handleCloseFilterDialog = () => {
        setFilterDialogOpen(false);
    };

    const handleApplyFilters = () => {
        setStatusFilter(localStatusFilter);
        setCampaignIdFilter(localCampaignIdFilter);
        setSelectedSenderEmail(localSelectedSenderEmail);
        setCurrentParams({ limit: 10, page: 0 });
        setFilterDialogOpen(false);
    };

    const handleClearFilters = () => {
        setLocalStatusFilter('');
        setLocalCampaignIdFilter('');
        setLocalSelectedSenderEmail(null);
        setStatusFilter('');
        setCampaignIdFilter('');
        setSelectedSenderEmail(null);
        setCurrentParams({ limit: 10, page: 0 });
        setFilterDialogOpen(false);
    };

    // Render attachment preview content
    const renderAttachmentPreview = () => {
        if (selectedAttachments.length === 0) {
            return (
                <div className={classes.attachmentPreviewContainer}>
                    <Typography className={classes.noAttachmentText}>No attachments available</Typography>
                </div>
            );
        }

        const currentAttachment = selectedAttachments[activeAttachmentTab];
        const url = getAttachmentUrl(currentAttachment);
        const filename = getAttachmentFilename(currentAttachment);
        const fileType = getFileType(url, filename);

        return (
            <div className={classes.attachmentPreviewContainer}>
                {fileType === 'image' ? (
                    <>
                        <img
                            src={url}
                            alt={filename}
                            className={classes.attachmentImage}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className={classes.attachmentFileInfo} style={{ display: 'none' }}>
                            {getFileIcon(fileType, classes)}
                            <Typography className={classes.attachmentFileName}>{filename}</Typography>
                            <ButtonOmni
                                onClick={() => handleDownloadAttachment(currentAttachment)}
                                label="Download"
                                size="small"
                                startIcon={<GetAppIcon />}
                                className={classes.downloadButton}
                            />
                        </div>
                    </>
                ) : fileType === 'video' ? (
                    <video
                        src={url}
                        controls
                        className={classes.attachmentVideo}
                    >
                        Your browser does not support the video tag.
                    </video>
                ) : (
                    <div className={classes.attachmentFileInfo}>
                        {getFileIcon(fileType, classes)}
                        <Typography className={classes.attachmentFileName}>{filename}</Typography>
                        <Typography className={classes.attachmentFileType}>
                            {filename.split('.').pop()?.toUpperCase() || 'FILE'}
                        </Typography>
                        <ButtonOmni
                            onClick={() => handleDownloadAttachment(currentAttachment)}
                            label="Download"
                            size="small"
                            startIcon={<GetAppIcon />}
                            className={classes.downloadButton}
                        />
                    </div>
                )}
            </div>
        );
    };

    // Calculate delivery percentage
    const calculateDeliveryPercentage = (row) => {
        const total = parseInt(row.total_count) || 0;
        const delivered = parseInt(row.delivered_count) || 0;
        const success = parseInt(row.success_count) || 0;
        if (total === 0) return '0%';
        return (((delivered + success) / total) * 100).toFixed(1) + '%';
    };


    // Get percentage color class
    const getPercentageColor = (percentage) => {
        const value = parseFloat(percentage);
        if (value >= 80) return { backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#16A34A' };
        if (value >= 50) return { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#D97706' };
        return { backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#DC2626' };
    };

    const headCells = [
        { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
        { id: 'campaign_id', numeric: false, disablePadding: false, label: 'Campaign Id' },
        {
            id: 'sender_email',
            numeric: false,
            disablePadding: false,
            label: 'Sender Email',
            email: true
        },
        {
            id: 'receiver_email',
            numeric: false,
            disablePadding: false,
            label: 'Receiver Email',
            email: true
        },
        {
            id: 'subject',
            numeric: false,
            disablePadding: false,
            label: 'Subject',
            msg: true,
            truncateLength: 30
        },
        {
            id: 'status',
            numeric: false,
            disablePadding: false,
            label: 'Status',
            customRow: row => (
                <span className={`${classes.statusChip} ${getStatusClass(row.status)}`}>
                    {capitalizeWords(row.status) || '-'}
                </span>
            )
        },
        {
            id: 'text',
            numeric: false,
            disablePadding: false,
            label: 'Email Text',
            msg: true,
            truncateLength: 30
        },
        {
            id: 'html',
            numeric: false,
            disablePadding: false,
            label: 'Email HTML',
            customRow: row => (
                row.html ? (
                    <span
                        className={classes.viewText}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewHtml(row);
                        }}
                    >
                        VIEW
                    </span>
                ) : (
                    <span style={{ color: theme.palette.text.secondary, fontSize: 13 }}>-</span>
                )
            )
        },
        {
            id: 'total_count',
            numeric: false,
            disablePadding: false,
            label: 'Total',
            customRow: row => row.total_count ?? 0
        },
        {
            id: 'success_count',
            numeric: false,
            disablePadding: false,
            label: 'Success',
            customRow: row => row.success_count ?? 0
        },
        {
            id: 'delivered_count',
            numeric: false,
            disablePadding: false,
            label: 'Delivered',
            customRow: row => row.delivered_count ?? 0
        },
        {
            id: 'received_count',
            numeric: false,
            disablePadding: false,
            label: 'Received',
            customRow: row => row.received_count ?? 0
        },
        {
            id: 'queue_count',
            numeric: false,
            disablePadding: false,
            label: 'Queue',
            customRow: row => row.queue_count ?? 0
        },
        {
            id: 'failed_count',
            numeric: false,
            disablePadding: false,
            label: 'Failed',
            customRow: row => row.failed_count ?? 0
        },
        {
            id: 'compliant_count',
            numeric: false,
            disablePadding: false,
            label: 'Compliant',
            customRow: row => row.compliant_count ?? 0
        },
        {
            id: 'delivery_percentage',
            numeric: false,
            disablePadding: false,
            label: 'Delivery %',
            customRow: row => {
                const percentage = calculateDeliveryPercentage(row);
                const colorStyle = getPercentageColor(percentage);
                return (
                    <span className={classes.percentageChip} style={colorStyle}>
                        {percentage}
                    </span>
                );
            }
        },
        {
            id: 'attachments',  // Changed from 'attachment_count'
            numeric: false,
            disablePadding: false,
            label: 'Attachments',
            customRow: row => {
                const count = getAttachmentCount(row.attachments);
                return (
                    <span className={classes.attachmentCountChip}>
                        <AttachFileIcon style={{ fontSize: 14 }} />
                        {count}
                    </span>
                );
            }
        },
        {
            id: 'view_attachments',
            numeric: false,
            disablePadding: false,
            label: 'View Attachments',
            customRow: row => {
                const count = getAttachmentCount(row.attachments);
                return count > 0 ? (
                    <span
                        className={classes.viewText}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewAttachments(row);
                        }}
                    >
                        VIEW
                    </span>
                ) : (
                    <span style={{ color: theme.palette.text.secondary, fontSize: 13 }}>-</span>
                );
            }
        },

        {
            id: 'dt',
            numeric: false,
            disablePadding: false,
            label: 'Date',
            dt: true
        },
        {
            id: 'error_message',
            numeric: false,
            disablePadding: false,
            label: 'Response Message',
            msg: true,
            truncateLength: 30
        },
        { id: 'download', numeric: false, disablePadding: false, label: 'Download' }
    ];

    useEffect(() => {
        if (outboxType) {
            fetchData();
        }
    }, [currentParams, dateRange, outboxType, statusFilter, campaignIdFilter, selectedSenderEmail]);

    // HTML Preview Modal Content
    const htmlPreviewContent = selectedEmail && (
        selectedEmail.html ? (
            <div
                className={classes.htmlPreviewContent}
                dangerouslySetInnerHTML={{ __html: extractBodyContent(selectedEmail.html) }}
                onClick={(e) => {
                    if (e.target.tagName === 'A' && e.target.href) {
                        e.preventDefault();
                        window.open(e.target.href, '_blank', 'noopener,noreferrer');
                    }
                }}
            />
        ) : (
            <div className={classes.noHtmlText}>No HTML content available</div>
        )
    );

    // Attachment Preview Modal Content
    const attachmentPreviewContent = (
        <>
            {selectedAttachments.length > 0 && (
                <div className={classes.attachmentTabsContainer}>
                    <Tabs
                        value={activeAttachmentTab}
                        onChange={handleAttachmentTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        {selectedAttachments.map((attachment, index) => {
                            const filename = getAttachmentFilename(attachment);
                            const truncatedName = filename.length > 20
                                ? `${filename.substring(0, 17)}...`
                                : filename;
                            return (
                                <Tab
                                    key={index}
                                    label={truncatedName}
                                    className={classes.attachmentTab}
                                />
                            );
                        })}
                    </Tabs>
                </div>
            )}
            {renderAttachmentPreview()}
        </>
    );

    // Filter Dialog Content
    const filterContent = (
        <div className={classes.filterContent}>
            <div className={classes.filterRow}>
                <FormFieldOmni
                    size="small"
                    label="Campaign ID"
                    variant="outlined"
                    value={localCampaignIdFilter}
                    onChange={(e) => setLocalCampaignIdFilter(e.target.value)}
                    fullWidth
                    placeholder="Search by Campaign ID"
                    className={classes.filterFieldHalf}
                />
                <FormFieldOmni
                    size="small"
                    label="Status"
                    variant="outlined"
                    value={localStatusFilter}
                    onChange={(e) => setLocalStatusFilter(e.target.value)}
                    type="select"
                    options={statusOptions}
                    fullWidth
                    className={classes.filterFieldHalf}
                />
            </div>
            <div className={classes.filterRow}>
                <div className={classes.filterFieldFull}>
                    <Typography style={{ fontSize: '0.875rem', color: theme.palette.text.secondary, fontWeight: 500, marginBottom: 4 }}>
                        Sender Email
                    </Typography>
                    <SearchableDropdown
                        label=""
                        options={formattedSenderEmailOptions}
                        value={localSelectedSenderEmail}
                        onChange={(newValue) => setLocalSelectedSenderEmail(newValue)}
                        placeholder={loadingSenderEmails ? "Loading sender emails..." : "Select sender email..."}
                        fullWidth
                        disabled={loadingSenderEmails}
                    />
                    {loadingSenderEmails && (
                        <Typography className={classes.loadingText}>Loading sender emails...</Typography>
                    )}
                </div>
            </div>
        </div>
    );

    // Filter Dialog Actions
    const filterActions = (
        <div className={classes.buttonContainer}>
            <Box display="flex" style={{ gap: 8 }}>
                <ButtonOmni onClick={handleClearFilters} size="small" variant="outlined" label="Clear" />
            </Box>
            <Box display="flex" style={{ gap: 8 }}>
                <ButtonOmni onClick={handleCloseFilterDialog} size="small" variant="outlined" label="Cancel" />
                <ButtonOmni onClick={handleApplyFilters} size="small" label="Apply" />
            </Box>
        </div>
    );

    return (
        <>
            <CSVLink
                ref={csvLink}
                data={dataForExport}
                filename={generateFileName()}
                style={{ display: 'none' }}
            />

            {/* HTML Preview Modal */}
            <ModalOmni
                open={htmlPreviewOpen}
                handleClose={handleCloseHtmlPreview}
                title="Email HTML Preview"
                content={htmlPreviewContent}
                customWidth={700}
                maxWidth="md"
                actions={
                    <ButtonOmni
                        onClick={handleCloseHtmlPreview}
                        variant="outlined"
                        label="Close"
                        size="small"
                    />
                }
            />

            {/* Attachment Preview Modal */}
            <ModalOmni
                open={attachmentPreviewOpen}
                handleClose={handleCloseAttachmentPreview}
                title={`Attachments (${selectedAttachments.length})`}
                content={attachmentPreviewContent}
                customWidth={700}
                maxWidth="md"
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        {selectedAttachments.length > 0 && (
                            <ButtonOmni
                                onClick={() => handleDownloadAttachment(selectedAttachments[activeAttachmentTab])}
                                label="Download"
                                size="small"
                                startIcon={<GetAppIcon />}
                            />
                        )}
                        <ButtonOmni
                            onClick={handleCloseAttachmentPreview}
                            variant="outlined"
                            label="Close"
                            size="small"
                        />
                    </Box>
                }
            />

            {/* Filter Dialog */}
            <ModalOmni
                open={filterDialogOpen}
                handleClose={handleCloseFilterDialog}
                title="Filters"
                content={filterContent}
                actions={filterActions}
                customWidth={500}
            />

            {/* Header Row */}
            <div className={classes.headerRow}>
                <div className={classes.leftControls}>
                    <div className={classes.selectContainer}>
                        <FormControl variant="outlined" size="small">
                            <Select
                                value={outboxType}
                                onChange={handleTypeChange}
                                displayEmpty
                                className={classes.typeSelect}
                            >
                                <MenuItem value="push">Push</MenuItem>
                                <MenuItem value="schedule">Campaign</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    {outboxType && (
                        <div className={classes.datePickerContainer}>
                            <DateRangePickerOmni
                                startDate={dateRange.startDate}
                                endDate={dateRange.endDate}
                                onDateChange={handleDateChange}
                                color="black"
                            />
                        </div>
                    )}
                </div>
                <div className={classes.rightControls}>
                    {outboxType && (
                        <>
                            <Tooltip title="Refresh" arrow>
                                <IconButton
                                    className={classes.refreshButton}
                                    onClick={handleRefresh}
                                    disabled={isLoading}
                                >
                                    <SyncIcon />
                                </IconButton>
                            </Tooltip>

                            {/* Filter Button with Badge */}
                            <Badge badgeContent={getActiveFilterCount()} color="primary">
                                <ButtonOmniNew
                                    onClick={handleOpenFilterDialog}
                                    size="small"
                                    variant="outlined"
                                    buttonType="filters"
                                    label={'Filters'}
                                />
                            </Badge>
                        </>
                    )}
                </div>
            </div>

            {/* Chart and Stats Section - Shows for all outbox types (push, schedule, all) */}
            {outboxType && (
                <div className={classes.chartSection}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={4}>
                            <EmailCampaignChart emailData={data} />
                        </Grid>
                        <Grid item xs={12} md={6} lg={8}>
                            <Paper className={classes.overviewCard}>
                                <Typography variant="h6" className={classes.overviewTitle}>
                                    <TrendingUpIcon style={{ color: theme.palette.primary.main }} />
                                    Outbox Overview
                                </Typography>
                                <div className={classes.statsGrid}>
                                    {statsConfig.map((stat, index) => (
                                        <Box
                                            key={index}
                                            className={classes.statCard}
                                            style={{ backgroundColor: stat.bgColor }}
                                        >
                                            <div
                                                className={classes.statIconWrapper}
                                                style={{ backgroundColor: stat.iconBg }}
                                            >
                                                {React.cloneElement(stat.icon, { style: { color: stat.color } })}
                                            </div>
                                            <Typography className={classes.statValue} style={{ color: stat.color }}>
                                                {stat.value.toLocaleString()}
                                            </Typography>
                                            <Typography className={classes.statLabel} style={{ color: theme.palette.text.secondary }}>
                                                {stat.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </div>
                            </Paper>
                        </Grid>
                    </Grid>
                </div>
            )}



            {/* Table Section */}
            {!outboxType ? (
                <div className={classes.tableSection}>
                    <div className={classes.placeholderText}>
                        Please select an outbox type to view emails
                    </div>
                </div>
            ) : (
                <>
                    {data && data.length > 0 && (
                        <Box display="flex" justifyContent="flex-end" marginBottom={3}>
                            <ButtonOmniNew
                                onClick={handleExport}
                                buttonType="export"
                                label={'Export'}
                                variant="outlined"
                                size="small"
                                disabled={isExporting}
                                loading={isExporting}
                            />
                        </Box>
                    )}
                    <div className={classes.tableSection}>
                        <TableOmniNewAnimated
                            loading={isLoading}
                            rows={data}
                            headCells={headCells}
                            perPageRows={currentParams.limit}
                            curPage={currentParams.page}
                            setCurrentParams={setCurrentParams}
                            totalItems={totalItems}
                            emptyMessage="No Emails Found"
                            onDownloadClick={handleDownloadSingleRow}
                        />
                    </div>
                </>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default OutboxTab;